"use client";

import React from "react";
import Translations from "@translate/components/Member/Subscription/SubscriptionCard/PurchaseActions/CheckoutConsent";
import { cn } from "utils";
import { agreement } from "@const/agreement";
import { MemberProduct } from "@features/api/memberApi";
import { useBridge } from "@/src/lib/bridge/useBridge";
import { I18nUtil } from "@utils/i18n";
import {
  buildConsentCopy,
  buildRefundPolicyCopy,
} from "@utils/member/checkoutConsent";
import { openExternal } from "@utils/member/openExternal";

const I18n = I18nUtil.createScoped(Translations);

const LINK_CLASS =
  "underline decoration-solid underline-offset-3 cursor-pointer bg-transparent border-0 p-0 hover:text-content_primary transition-colors";

interface CheckoutConsentProps {
  /** 正价产品（周期与续费价来源） */
  product?: MemberProduct | null;
  /** 试用 SKU（实际结算）；普通订阅不传 */
  trialProduct?: MemberProduct | null;
  /** 按实际下单渠道解析的试用天数 */
  trialDays?: number;
  /** 主按钮最终文案（已翻译） */
  ctaLabel: string;
  className?: string;
}

/**
 * 点击同意文案（EDG-552，fe-spec §4.6 F1-copy，CONSENT_MODE="button"：按钮即同意）。
 *
 * 文本 = 扣款条款（`buildConsentCopy`，随 CONSENT_VERSION 版本化）+ 不退部分周期费用条款
 * （沿用原 H5 `SubscribableTerms` 的法务句，并入同一版本）+ Terms / Privacy。
 * 条款缺信息时不渲染（宁可不展示也不展示错的扣款承诺），调用方据此禁用下单。
 *
 * 偏差：勾选框模式（CONSENT_MODE="checkbox"）未实现 UI —— 无设计来源且当前为死分支，
 * 法务结论（GAP-8）定稿后随设计一起补。
 */
export const CheckoutConsent: React.FC<CheckoutConsentProps> = ({
  product,
  trialProduct,
  trialDays,
  ctaLabel,
  className,
}) => {
  const { send, isInApp } = useBridge();
  const copy = product
    ? buildConsentCopy({ product, trialProduct, trialDays, ctaLabel })
    : null;
  if (!copy) {
    return null;
  }
  const open = (url: string) => openExternal(url, { isApp: isInApp, send });
  return (
    <p
      className={cn("text-content_tertiary body_s text-center", className)}
      data-testid="checkout-consent"
    >
      {copy.text} {buildRefundPolicyCopy()}{" "}
      <button
        type="button"
        data-openreplay-label="member_checkoutconsent_terms"
        className={LINK_CLASS}
        onClick={() => open(agreement.termsLink)}
      >
        {I18n.t("Terms of Use")}
      </button>{" "}
      ·{" "}
      <button
        type="button"
        data-openreplay-label="member_checkoutconsent_privacy"
        className={LINK_CLASS}
        onClick={() => open(agreement.privacyLink)}
      >
        {I18n.t("Privacy Policy")}
      </button>
    </p>
  );
};
